// =============================================================================
// analytics.ts - read-side queries for the Analyse page + calendar YTD stat
// =============================================================================
// Thin wrappers over two SECURITY DEFINER RPCs in the booking schema:
//   fn_admin_analytics_breakdown  -> per (location, experience) totals
//   fn_admin_analytics_timeseries -> per (period bucket, experience) series
// Both aggregate at the departure_experience (pick) level, so `guests` is a
// unique headcount. See the migration 20260615120000_booking_admin_analytics_rpcs.
//
// revenue_cents is CASH, not list price (20260803093110): the booking's captured
// payments (stripe + cash + card_terminal) split over its picks pro rata. A
// booking paid with a voucher therefore only contributes the top-up. The sale of
// a gift voucher comes back as its own row with location_id/experience_id NULL
// and experience_name 'Vouchers', on the sale date — only for the 'created'
// basis and only when no location is selected (a voucher has neither).
//
// Counter sales (20260921130000 + 20260921190000): payments with a venue but
// no booking (the CCV terminal taps imported for Eindhoven) are folded into
// the venue's experiences per calendar month, in the ratio of that month's
// booked tickets (lossless rounding; a month without bookings goes to the
// venue's first experience, else Egypt). Their guests are the ticket
// ESTIMATE stored on the row (amount / ticket price), bookings = taps, money
// = amount. Shown on both date bases. An older function deploy returned them
// as one 'Balie (pin)' row per venue (experience_id NULL, location_id set).
//
// Every money key also comes ex BTW (20260921133014) and each row carries the
// tickets a redeemed voucher covered; viewBreakdownRow / viewTimeseriesRow
// below turn a row into what the Analyse page shows (ex BTW, redeemed
// vouchers out unless asked for).
//
// Rows are split by partner (20261105170000): partner_id / partner_name are set
// on what was sold through a partner's booking form and null on everything else,
// including vouchers and counter sales. inChannel filters on them; an older
// function deploy has neither key, so every row counts as our own.
// =============================================================================
import { supabase } from './supabase';

// Which date drives filtering/bucketing.
//   'run'     = when guests visit (slot start)
//   'created' = sales pace (bookings.created_at)
export type DateBasis = 'run' | 'created';
export type Bucket = 'hour' | 'day' | 'week' | 'month';

// Lifecycle sets. The Analyse page counts real sales; the calendar header YTD
// passes the calendar set so its number lines up with "deze week / deze maand".
export const SOLD_LIFECYCLE = ['confirmed', 'arrived', 'no_show'];
export const CALENDAR_LIFECYCLE = ['draft', 'confirmed', 'arrived'];

// location_id / experience_id are null on the synthetic "Vouchers" row.
// experience_id alone is null on the per-venue "Balie (pin)" counter-sales row
// of an older function deploy (see above).
export interface BreakdownRow {
  location_id: string | null;
  location_name: string;
  experience_id: string | null;
  experience_name: string;
  partner_id?: string | null;
  partner_name?: string | null;
  guests: number;
  bookings: number;
  departures: number;
  revenue_cents: number;
  // Since migration booking_analytics_breakdown_gross_and_charged (2026-08-27):
  //   list_cents    = gross at list price (sum of pick price_cents)
  //   charged_cents = bookings.total_cents split over picks (after discounts)
  //   discounts     = list_cents - charged_cents
  //   voucher_tickets = tickets sold, on the synthetic Vouchers row only
  // Optional so an older function deploy still renders (the location table
  // then falls back to revenue_cents and shows no discounts).
  list_cents?: number;
  charged_cents?: number;
  voucher_tickets?: number;
  // Since migration booking_analytics_ex_vat_voucher_tickets (2026-09-21):
  //   vat_rate_pct                  rate behind the ex BTW keys of this row
  //   *_ex_vat_cents                the same money ex BTW (rounded per row)
  //   voucher_tickets_redeemed      tickets covered by a redeemed flat or 100%
  //                                 code (gift voucher, tegoedbon, comp)
  //   voucher_redeemed_cents        their list value (incl. / ex BTW)
  // The function does NOT subtract the redeemed share: the page does, unless
  // the "Ingewisselde vouchers" checkbox is on. Optional so an older function
  // deploy still renders (ex BTW is then computed here at 9%).
  vat_rate_pct?: number;
  revenue_ex_vat_cents?: number;
  list_ex_vat_cents?: number;
  charged_ex_vat_cents?: number;
  voucher_tickets_redeemed?: number;
  voucher_redeemed_cents?: number;
  voucher_redeemed_ex_vat_cents?: number;
  // Since migration booking_analytics_refund_basis (20261015120000): a paid
  // cancelled booking stays a sale and a refund comes back as a minus on the
  // refund day ('created' basis) or the visit day ('run' basis). guests and
  // revenue are already NET of these; the keys below are the refunded part,
  // positive. Absent on an older function deploy.
  refunded_tickets?: number;
  refunded_cents?: number;
  refunded_ex_vat_cents?: number;
}

export interface TimeseriesRow {
  period: string;          // bucket start (Europe/Amsterdam): 'YYYY-MM-DD', or 'YYYY-MM-DD HH:00' for the hour bucket
  experience_id: string | null;
  experience_name: string;
  partner_id?: string | null;
  partner_name?: string | null;
  guests: number;
  bookings: number;
  revenue_cents: number;
  revenue_ex_vat_cents?: number;
  voucher_tickets_redeemed?: number;
  // Tickets on the gift vouchers sold in this bucket (Vouchers row only, 0
  // elsewhere; 20261013130912). Absent on an older function deploy.
  voucher_tickets?: number;
  // Refunded part, positive; guests/revenue are net of it (20261015120000).
  refunded_tickets?: number;
  refunded_cents?: number;
  refunded_ex_vat_cents?: number;
  // revenue_ex_vat_cents before the per-row rounding (4 decimals), so a sum of
  // rows can round once (20261022170000). Absent on an older function deploy.
  revenue_ex_vat_exact?: number;
}

// NL low VAT rate (entertainment); every venue is on it today. Only used when
// a row predates the ex BTW keys.
export const DEFAULT_VAT_RATE_PCT = 9;

// Gross incl. BTW -> ex BTW, the formula the mail and ticket PDF use.
export function exVat(cents: number, ratePct: number = DEFAULT_VAT_RATE_PCT): number {
  return Math.round((cents * 100) / (100 + ratePct));
}

// What the page shows for one breakdown row: ex BTW money, and tickets with
// the redeemed-voucher share taken out unless showRedeemed is on. A redeemed
// voucher is not a sale (the sale was the voucher purchase), so its tickets
// leave the count and their list value leaves both Omzet and Korting; Totaal
// (what the customer paid) never moves.
//
// guests on the Vouchers row = the voucher tickets SOLD (the function returns
// guests 0 there and the count as voucher_tickets, which is 0 on every other
// row), so the Tickets card, the donut and the tables all count a voucher
// ticket at sale (owner's ask 2026-10-13).
export interface RowView {
  guests: number;
  bookings: number;
  revenue_ex_vat_cents: number;   // captured cash ex BTW
  gross_ex_vat_cents: number;     // list price ex BTW
  discount_ex_vat_cents: number;  // gross - charged, ex BTW
  total_ex_vat_cents: number;     // charged ex BTW
  refund_ex_vat_cents: number;    // refunded in this cell, positive (already netted out of guests and revenue)
}

export function viewBreakdownRow(r: BreakdownRow, showRedeemed: boolean): RowView {
  const rate = r.vat_rate_pct ?? DEFAULT_VAT_RATE_PCT;
  const revenue = r.revenue_ex_vat_cents ?? exVat(r.revenue_cents, rate);
  // Older function deploys carry neither list nor charged: fall back to
  // captured cash for both, which renders the tables without a discount line.
  const gross = r.list_ex_vat_cents ?? exVat(r.list_cents ?? r.revenue_cents, rate);
  const charged = r.charged_ex_vat_cents ?? exVat(r.charged_cents ?? r.revenue_cents, rate);
  const redeemedTickets = showRedeemed ? 0 : (r.voucher_tickets_redeemed ?? 0);
  const redeemedCents = showRedeemed
    ? 0
    : (r.voucher_redeemed_ex_vat_cents ?? exVat(r.voucher_redeemed_cents ?? 0, rate));
  const discount = gross - charged;
  // Never let Korting go negative (a walk-in price edit on top of a voucher):
  // take out at most the discount that is there, and keep gross - discount = total.
  const takeOut = Math.max(0, Math.min(redeemedCents, discount));
  return {
    // Floor at minus the refunded tickets, not 0: a day with only a refund
    // in this cell must show the minus.
    guests: Math.max(-(r.refunded_tickets ?? 0), r.guests - redeemedTickets) + (r.voucher_tickets ?? 0),
    bookings: r.bookings,
    revenue_ex_vat_cents: revenue,
    gross_ex_vat_cents: gross - takeOut,
    discount_ex_vat_cents: discount - takeOut,
    total_ex_vat_cents: charged,
    refund_ex_vat_cents: r.refunded_ex_vat_cents ?? exVat(r.refunded_cents ?? 0, rate),
  };
}

export interface SeriesView {
  guests: number;
  bookings: number;
  revenue_ex_vat_cents: number;
}

export function viewTimeseriesRow(r: TimeseriesRow, showRedeemed: boolean): SeriesView {
  const redeemed = showRedeemed ? 0 : (r.voucher_tickets_redeemed ?? 0);
  return {
    guests: Math.max(-(r.refunded_tickets ?? 0), r.guests - redeemed) + (r.voucher_tickets ?? 0),
    bookings: r.bookings,
    revenue_ex_vat_cents: r.revenue_ex_vat_cents ?? exVat(r.revenue_cents),
  };
}

// Sales channel on the Analyse page: everything, our own sales, or one partner
// (its id). Vouchers and counter sales belong to our own.
export const CHANNEL_ALL = 'all';
export const CHANNEL_OWN = 'own';
export const CHANNEL_OWN_LABEL = 'Eigen verkoop';

export interface PartnerOption {
  id: string;
  name: string;
}

export function inChannel(r: { partner_id?: string | null }, channel: string): boolean {
  if (channel === CHANNEL_ALL) return true;
  if (channel === CHANNEL_OWN) return !r.partner_id;
  return r.partner_id === channel;
}

// Partners seen so far plus the ones in these rows, by name. Returns `known`
// itself when nothing is new, so a state update with it does not re-render.
export function mergePartners(
  known: PartnerOption[],
  rows: { partner_id?: string | null; partner_name?: string | null }[],
): PartnerOption[] {
  const fresh = new Map<string, string>();
  for (const r of rows) {
    if (r.partner_id && !known.some((p) => p.id === r.partner_id)) {
      fresh.set(r.partner_id, r.partner_name ?? 'Partner');
    }
  }
  if (fresh.size === 0) return known;
  return [...known, ...[...fresh].map(([id, name]) => ({ id, name }))]
    .sort((a, b) => a.name.localeCompare(b.name, 'nl'));
}

export interface BreakdownOpts {
  locationId?: string | null;
  dateBasis?: DateBasis;
  lifecycle?: string[];
}

export interface TimeseriesOpts {
  locationId?: string | null;
  bucket?: Bucket;
  dateBasis?: DateBasis;
  lifecycle?: string[];
}

// One row per (location, experience) for the [from, to] range (inclusive).
export async function fetchAnalyticsBreakdown(
  fromDate: string,
  toDate: string,
  opts: BreakdownOpts = {},
): Promise<BreakdownRow[]> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_analytics_breakdown', {
      p_from: fromDate,
      p_to: toDate,
      p_location_id: opts.locationId ?? null,
      p_date_basis: opts.dateBasis ?? 'run',
      p_lifecycle_statuses: opts.lifecycle ?? SOLD_LIFECYCLE,
    });
  if (error) throw error;
  if (!Array.isArray(data)) return [];
  return data as BreakdownRow[];
}

// One row per (period bucket, experience), aggregated across the location scope
// (locationId null/omitted = all locations combined).
export async function fetchAnalyticsTimeseries(
  fromDate: string,
  toDate: string,
  opts: TimeseriesOpts = {},
): Promise<TimeseriesRow[]> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_analytics_timeseries', {
      p_from: fromDate,
      p_to: toDate,
      p_location_id: opts.locationId ?? null,
      p_bucket: opts.bucket ?? 'day',
      p_date_basis: opts.dateBasis ?? 'created',
      p_lifecycle_statuses: opts.lifecycle ?? SOLD_LIFECYCLE,
    });
  if (error) throw error;
  if (!Array.isArray(data)) return [];
  return data as TimeseriesRow[];
}

// Convenience: total guests across all breakdown rows.
export function sumGuests(rows: BreakdownRow[]): number {
  return rows.reduce((n, r) => n + (r.guests ?? 0), 0);
}

// Bare headcount for the calendar header YTD stat. Deliberately NOT the
// breakdown RPC: that one requires booking_view_analytics, and the calendar is
// open to every operator. fn_admin_ytd_guests returns a single integer — no
// revenue, no per-experience rows — so it needs no analytics permission.
export async function fetchYtdGuests(
  fromDate: string,
  toDate: string,
  opts: { locationId?: string | null; lifecycle?: string[] } = {},
): Promise<number> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_ytd_guests', {
      p_from: fromDate,
      p_to: toDate,
      p_location_id: opts.locationId ?? null,
      p_lifecycle_statuses: opts.lifecycle ?? CALENDAR_LIFECYCLE,
    });
  if (error) throw error;
  return typeof data === 'number' ? data : 0;
}
